import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ExternalLink, FlaskConical, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Chip, Loader, PageHeader, Reveal } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const StatusBadge = ({ p }) => {
  const st = p.status || {};
  if (!p.configured) return <span className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-500">Sin clave</span>;
  if (p.cooling) return <span className="rounded-full bg-streak-soft px-2.5 py-1 text-[11px] font-semibold text-streak-ink">En pausa (agotado/fallo)</span>;
  if (st.last_ok && (!st.last_error_at || st.last_ok > st.last_error_at)) return <span className="rounded-full bg-food-soft px-2.5 py-1 text-[11px] font-semibold text-food-ink">Funcionando</span>;
  if (st.last_error_at) return <span className="rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700">Último intento falló</span>;
  return <span className="rounded-full bg-care-soft px-2.5 py-1 text-[11px] font-semibold text-care-ink">Listo</span>;
};

const ProviderRow = ({ p, i, total, edit, onEdit, onMove, onTest, onReset }) => (
  <Reveal delay={i * 0.03} className="card-clay p-5" >
    <div className="flex flex-wrap items-center gap-3" data-testid={`ai-provider-${p.id}`}>
      <div className="flex flex-col">
        <button disabled={i === 0} onClick={() => onMove(-1)} data-testid={`ai-provider-up-${p.id}`} className="rounded p-0.5 disabled:opacity-20"><ArrowUp className="h-4 w-4" /></button>
        <button disabled={i === total - 1} onClick={() => onMove(1)} data-testid={`ai-provider-down-${p.id}`} className="rounded p-0.5 disabled:opacity-20"><ArrowDown className="h-4 w-4" /></button>
      </div>
      <span className="font-display text-lg font-extrabold text-stone-300">{i + 1}</span>
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold">{p.name}</p>
        <p className="text-xs text-stone-500">Modelo: {edit.model || p.default_model || p.model} · {p.share ?? 0}% de respuestas{p.key_source ? ` · clave desde ${p.key_source === "env" ? "variables de entorno" : "este panel"}` : ""}</p>
      </div>
      <StatusBadge p={p} />
      <Switch checked={edit.enabled} onCheckedChange={(v) => onEdit({ enabled: v })} data-testid={`ai-provider-enabled-${p.id}`} />
    </div>
    <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
      <label className="flex items-center gap-2">Prioridad
        <select value={edit.priority} onChange={(e) => onEdit({ priority: Number(e.target.value) })} className="h-9 rounded-xl border border-input bg-white px-2" data-testid={`ai-provider-priority-${p.id}`}>
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <label className="flex items-center gap-2">Peso
        <input type="range" min={1} max={10} value={edit.weight} onChange={(e) => onEdit({ weight: Number(e.target.value) })} className="accent-ink" data-testid={`ai-provider-weight-${p.id}`} />
        <span className="w-5 font-display font-bold">{edit.weight}</span>
      </label>
    </div>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      <Input type="password" placeholder={p.api_key_masked ? `Clave guardada ${p.api_key_masked} (deja vacío para mantener)` : p.key_optional ? "API key (opcional)" : "Pega aquí tu API key"}
        value={edit.api_key ?? ""} onChange={(e) => onEdit({ api_key: e.target.value })} className="h-11 rounded-2xl" data-testid={`ai-provider-key-${p.id}`} />
      <Input placeholder={`Modelo (por defecto ${p.default_model || p.model})`} value={edit.model} onChange={(e) => onEdit({ model: e.target.value })} className="h-11 rounded-2xl" data-testid={`ai-provider-model-${p.id}`} />
      {p.id === "custom" && <Input placeholder="URL base, ej. http://mi-servidor:3001/v1 u http://host:11434/v1" value={edit.base_url} onChange={(e) => onEdit({ base_url: e.target.value })} className="h-11 rounded-2xl sm:col-span-2" data-testid="ai-provider-baseurl-custom" />}
    </div>
    {p.status?.last_error && <p className="mt-3 truncate text-xs text-red-600" title={p.status.last_error}>Último error: {p.status.last_error}</p>}
    <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
      <button onClick={onTest} data-testid={`ai-provider-test-${p.id}`} className="flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-2 text-white transition-transform active:scale-95"><FlaskConical className="h-3.5 w-3.5" /> Probar</button>
      {p.cooling && <button onClick={onReset} data-testid={`ai-provider-reset-${p.id}`} className="flex items-center gap-1.5 rounded-full border border-stone-300 px-3.5 py-2"><RotateCcw className="h-3.5 w-3.5" /> Quitar pausa</button>}
      {p.key_url && <a href={p.key_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-full border border-stone-300 px-3.5 py-2" data-testid={`ai-provider-keylink-${p.id}`}><ExternalLink className="h-3.5 w-3.5" /> Conseguir clave</a>}
    </div>
  </Reveal>
);

const AiProviders = () => {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [order, setOrder] = useState([]);
  const [mode, setMode] = useState("routing");
  const hydrate = (d) => {
    setData(d);
    setOrder(d.order);
    setMode(d.mode);
    setEdits(Object.fromEntries(d.providers.map((p) => [p.id, {
      enabled: p.enabled,
      // Solo el override; vacío = usar default actual (evita congelar slugs viejos)
      model: p.model_override ?? "",
      base_url: p.base_url || "",
      priority: p.priority,
      weight: p.weight,
      api_key: undefined,
    }])));
  };
  useEffect(() => { api.get("/admin/ai-providers").then((r) => hydrate(r.data)); }, []);
  if (!data) return <Loader />;
  const byId = Object.fromEntries(data.providers.map((p) => [p.id, p]));
  const move = (i, d) => { const o = [...order]; [o[i], o[i + d]] = [o[i + d], o[i]]; setOrder(o); };
  const save = async () => {
    const providers = Object.fromEntries(Object.entries(edits).map(([k, v]) => [k, { ...v, api_key: v.api_key ? v.api_key : null }]));
    try { const r = await api.put("/admin/ai-providers", { mode, order, providers }); hydrate(r.data); toast.success("Conectores guardados"); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async (id) => {
    const r = await api.post(`/admin/ai-providers/${id}/test`);
    r.data.ok ? toast.success(`${byId[id].name}: responde «${r.data.reply}»`) : toast.error(`${byId[id].name}: ${r.data.error}`);
    api.get("/admin/ai-providers").then((x) => setData(x.data));
  };
  const reset = async (id) => { await api.post(`/admin/ai-providers/${id}/reset`); api.get("/admin/ai-providers").then((x) => setData(x.data)); };
  return (
    <section className="space-y-3" data-testid="admin-ai-section">
      <h2 className="text-lg font-bold">Conectores de IA del Coach</h2>
      <div className="flex flex-wrap gap-2">
        <Chip active={mode === "routing"} onClick={() => setMode("routing")} testid="ai-mode-routing">Routing mixto</Chip>
        <Chip active={mode === "cascade"} onClick={() => setMode("cascade")} testid="ai-mode-cascade">Cascada</Chip>
      </div>
      <p className="text-sm text-stone-600" data-testid="ai-mode-help">
        {mode === "routing"
          ? "Routing mixto: cada petición se reparte al azar entre los proveedores sanos de la prioridad más alta (1), según su peso. Si uno falla o se agota, pasa al siguiente y queda en pausa; cuando no queda ninguno en esa prioridad, baja a la 2, etc. Así se reparten los límites gratuitos."
          : "Cascada: siempre se usa el primero de la lista; solo se pasa al siguiente si falla o se agota."}
        {" "}Guarda antes de probar una clave nueva.
      </p>
      {order.map((id, i) => byId[id] && (
        <ProviderRow key={id} p={byId[id]} i={i} total={order.length} edit={edits[id]} onEdit={(p) => setEdits({ ...edits, [id]: { ...edits[id], ...p } })}
          onMove={(d) => move(i, d)} onTest={() => test(id)} onReset={() => reset(id)} />
      ))}
      <button onClick={save} data-testid="admin-ai-save-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-semibold text-white transition-transform active:scale-95"><Save className="h-4 w-4" /> Guardar conectores</button>
    </section>
  );
};

const StravaConfig = () => {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ client_id: "", client_secret: "" });
  useEffect(() => { api.get("/admin/strava").then((r) => { setD(r.data); setF({ client_id: r.data.client_id, client_secret: "" }); }); }, []);
  if (!d) return null;
  const save = async () => {
    try { const r = await api.put("/admin/strava", { client_id: f.client_id, client_secret: f.client_secret || null }); setD(r.data); setF({ ...f, client_secret: "" }); toast.success("Strava configurado"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="card-clay space-y-4 p-5 sm:p-6" data-testid="admin-strava-section">
      <h2 className="text-lg font-bold">Strava (OAuth)</h2>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-stone-600">
        <li>Crea una app en <a className="font-semibold text-ink underline" href="https://www.strava.com/settings/api" target="_blank" rel="noreferrer">strava.com/settings/api</a>.</li>
        <li>En «Authorization Callback Domain» pon: <code className="rounded bg-stone-100 px-1.5 py-0.5" data-testid="admin-strava-domain">{d.callback_domain}</code></li>
        <li>Copia aquí el Client ID y el Client Secret.</li>
      </ol>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input placeholder="Client ID" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className="h-11 rounded-2xl" data-testid="admin-strava-client-id" />
        <Input type="password" placeholder={d.client_secret_masked ? `Secret guardado ${d.client_secret_masked}` : "Client Secret"} value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} className="h-11 rounded-2xl" data-testid="admin-strava-client-secret" />
      </div>
      <p className="text-xs text-stone-500">URL de redirección: {d.redirect_uri}</p>
      <button onClick={save} data-testid="admin-strava-save-btn" className="rounded-full bg-[#FC4C02] px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-95">Guardar Strava</button>
    </section>
  );
};

const ResetPassword = () => {
  const [f, setF] = useState({ email: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/users/reset-password", f);
      toast.success(`Contraseña restablecida para ${f.email}`);
      setF({ email: "", new_password: "" });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <section className="card-clay space-y-4 p-5 sm:p-6" data-testid="admin-reset-password-section">
      <h2 className="text-lg font-bold">Restablecer contraseña de un usuario</h2>
      <p className="text-sm text-stone-600">Útil si alguien no puede entrar. No envía email: solo cambia la clave en el servidor.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input type="email" placeholder="Email del usuario" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 rounded-2xl" data-testid="admin-reset-email" />
        <Input type="password" minLength={6} placeholder="Nueva contraseña (mín. 6)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="h-11 rounded-2xl" data-testid="admin-reset-password" />
      </div>
      <button disabled={busy || !f.email || f.new_password.length < 6} onClick={save} data-testid="admin-reset-save-btn" className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
        {busy ? "Guardando…" : "Restablecer"}
      </button>
    </section>
  );
};

export default function Admin() {
  return (
    <div className="space-y-8 pb-10" data-testid="admin-page">
      <PageHeader eyebrow="Administración" title="Conectores" subtitle="Configura aquí tus claves de IA gratuitas y la app de Strava. Las claves se guardan en el servidor y nunca se muestran completas." />
      <AiProviders />
      <StravaConfig />
      <ResetPassword />
    </div>
  );
}
